import { Color } from '../../features/visual-analytics/helpers/colors';
import { TpWebGL2DRenderDataSimpleLines, TpWebGLRenderDataLayer } from './interface-renderData';
import { TpWebGL2DRenderLayer, TpWebGl2DContext, WebGL2DRenderTypes } from './interfaces';
import { setWebGLZoomPan } from './utils';

export interface TpRenderSimpleLines {
  renderType: WebGL2DRenderTypes.SIMPLELINES;
  ctx: TpWebGl2DContext;
  layerId: string;
  lineCount: number;
  positionBuffer: any;
  color: Color;
}

function create(ctx: TpWebGl2DContext, dataInp: TpWebGLRenderDataLayer): TpRenderSimpleLines {
  const data = dataInp as TpWebGL2DRenderDataSimpleLines;
  const gl = ctx.gl;

  // Create a buffer for the vertices
  const positionBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, data.posits, gl.STATIC_DRAW);

  const renderCtx: TpRenderSimpleLines = {
    renderType: WebGL2DRenderTypes.SIMPLELINES,
    layerId: data.layerId,
    ctx,
    positionBuffer,
    color: data.color,
    lineCount: data.posits.length / 4,
  };

  return renderCtx;
}

function updateData(rendererInp: TpWebGL2DRenderLayer, dataInp: TpWebGLRenderDataLayer) {
  const renderer = rendererInp as TpRenderSimpleLines;
  const data = dataInp as TpWebGL2DRenderDataSimpleLines;
  const gl = renderer.ctx.gl;

  // NOTE: as a future improvement, we only update the buffer if the floatarray was changed (simple reference check)
  gl.bindBuffer(gl.ARRAY_BUFFER, renderer.positionBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, data.posits, gl.STATIC_DRAW);

  renderer.color = data.color;
  renderer.lineCount = data.posits.length / 4;
}

function performRender(webGlCtx: TpWebGl2DContext, renderCtxInp: TpWebGL2DRenderLayer) {
  const renderCtx = renderCtxInp as TpRenderSimpleLines;
  const gl = webGlCtx.gl;
  const myProgram = webGlCtx.programContextSimpleLines;
  gl.useProgram(myProgram.program);

  setWebGLZoomPan(webGlCtx, myProgram);

  gl.enable(gl.BLEND);
  gl.blendEquation(gl.FUNC_ADD);
  // gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA); // most logical choice for premultipliedAlpha ?
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

  gl.uniform4f(
    myProgram.locFixedColor!,
    renderCtx.color.r / 255,
    renderCtx.color.g / 255,
    renderCtx.color.b / 255,
    renderCtx.color.a / 255,
  );

  gl.lineWidth(1);

  // Vertices
  gl.enableVertexAttribArray(myProgram.locAttribPosition);
  gl.bindBuffer(gl.ARRAY_BUFFER, renderCtx.positionBuffer);
  gl.vertexAttribPointer(
    myProgram.locAttribPosition,
    2, // 2 components per iteration (because only two coordinates per vertex are provided)
    gl.FLOAT,
    false, // don't normalize the data
    0, // stride 0 = move forward size * sizeof(type) each iteration to get the next position
    0, // offset
  );

  gl.drawArrays(
    gl.LINES,
    0, // offset
    renderCtx.lineCount * 2,
  );
}

export const webGl2DColoredSimpleLines = {
  create,
  updateData,
  performRender,
};
